/**
 * Workbench System Map panel — Iter 290zp.15z02.
 *
 * Read-only visualisation of the AI-built engineering ledger that lives
 * on `draft.engineering_state`:
 *   • `system_flow[]` — the ordered list of process stages
 *     (inlet → softener → RO → CIP → outlet) with sizing & roles.
 *   • `decisions[]`  — load-bearing engineering decisions (e.g.
 *     "Skid-mounted with bypass loop", "5 micron pre-filter on inlet").
 *   • `open_questions[]` — things the AI still needs from the rep.
 *
 * This panel is intentionally read-only — the rep updates these through
 * StageChat conversation, not by clicking. The panel is the
 * non-chat representation of what Hidro currently believes about the
 * system. Polls every 6 seconds while expanded.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GitBranch, ChevronRight, AlertCircle, Loader2, Eye, EyeOff,
  Sparkles, HelpCircle, Pin, History, Droplets,
} from "lucide-react";
import api from "../../lib/api";


function _asText(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(_asText).join(", ");
  // Fallback: render object as compact JSON so we never crash on an
  // unexpected shape (decisions / open_questions can be arbitrary
  // structures coming from the AI extractor).
  try { return JSON.stringify(v); } catch { return String(v); }
}


function StageNode({ stage, idx, total }) {
  // Iter 290zp.15z02 — one process-stage rendered as a horizontal card.
  // Role colour-coded so the eye can scan the chain quickly.
  const roleColour = (() => {
    const r = (stage.role || "").toLowerCase();
    if (r.includes("source") || r.includes("inlet") || r.includes("raw")) return "bg-sky-500/15 border-sky-400/40 text-sky-200";
    if (r.includes("pre")) return "bg-cyan-500/15 border-cyan-400/40 text-cyan-200";
    if (r.includes("soft")) return "bg-violet-500/15 border-violet-400/40 text-violet-200";
    if (r.includes("ro") || r.includes("membrane")) return "bg-emerald-500/15 border-emerald-400/40 text-emerald-200";
    if (r.includes("uv") || r.includes("ozone") || r.includes("polish")) return "bg-amber-500/15 border-amber-400/40 text-amber-200";
    if (r.includes("storage") || r.includes("tank")) return "bg-indigo-500/15 border-indigo-400/40 text-indigo-200";
    if (r.includes("dosing") || r.includes("chem")) return "bg-rose-500/15 border-rose-400/40 text-rose-200";
    return "bg-white/10 border-white/15 text-white/80";
  })();

  return (
    <div className="flex items-start gap-1.5 min-w-0" data-testid={`systemmap-stage-${idx}`}>
      <div className={`rounded-md border px-2 py-1.5 min-w-[120px] max-w-[180px] ${roleColour}`}>
        <div className="text-[11px] font-semibold truncate" title={_asText(stage.name)}>
          {_asText(stage.name) || "(unnamed)"}
        </div>
        {stage.role && (
          <div className="text-[9px] uppercase tracking-wider opacity-80 truncate">{_asText(stage.role)}</div>
        )}
        <div className="text-[10px] mt-0.5 opacity-90 flex flex-wrap gap-x-1.5 gap-y-0.5">
          {stage.design_flow_lph != null && (
            <span title="Design flow (litres/hour)">{stage.design_flow_lph} L/h</span>
          )}
          {stage.in_diameter_mm != null && (
            <span title="Inlet pipe diameter">in Ø{stage.in_diameter_mm}</span>
          )}
          {stage.out_diameter_mm != null && (
            <span title="Outlet pipe diameter">out Ø{stage.out_diameter_mm}</span>
          )}
        </div>
      </div>
      {idx < total - 1 && (
        <ChevronRight className="w-3 h-3 mt-3 text-[#475569] shrink-0" />
      )}
    </div>
  );
}


function DecisionRow({ decision }) {
  const isSuperseded = !!decision.supersedes;
  return (
    <li
      className={`flex items-start gap-2 py-1 px-2 rounded text-[11px] ${
        isSuperseded
          ? "text-[#647087] line-through opacity-60"
          : "text-[#cbd5e1] bg-white/[0.02]"
      }`}
      title={isSuperseded ? "Superseded by a later decision" : undefined}
    >
      {isSuperseded ? (
        <History className="w-3 h-3 mt-0.5 shrink-0 text-[#475569]" />
      ) : (
        <Pin className="w-3 h-3 mt-0.5 shrink-0 text-[#1FB6C1]" />
      )}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-[10px] uppercase tracking-wider text-[#94a3b8] truncate">
          {_asText(decision.topic) || "—"}
        </div>
        <div className="break-words">{_asText(decision.value) || "(no detail)"}</div>
      </div>
    </li>
  );
}


export default function SystemMapPanel({ draft }) {
  const [state, setState] = useState(draft.engineering_state || null);
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(draft.updated_at || null);
  const pollRef = useRef(null);

  // Iter 290zp.15z02 — Re-pull just the engineering_state so the AI's
  // background updates show up without forcing a full draft reload.
  const load = useCallback(async () => {
    if (!draft?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/workbench/drafts/${draft.id}`,
        { headers: { "Cache-Control": "no-cache" }, params: { _: Date.now() } },
      );
      setState(data?.engineering_state || null);
      setUpdatedAt(data?.updated_at || null);
    } catch {
      // Silently keep prior state.
    } finally {
      setLoading(false);
    }
  }, [draft?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (collapsed) return undefined;
    const tick = () => { if (!document.hidden) load(); };
    pollRef.current = setInterval(tick, 6000);
    const onWake = () => tick();
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [load, collapsed]);

  const flow = state?.system_flow || [];
  const decisions = state?.decisions || [];
  const openQuestions = state?.open_questions || [];

  // Group decisions: active (not superseded) first, then the rest.
  const orderedDecisions = useMemo(() => {
    const active = decisions.filter((d) => !d.supersedes);
    const old = decisions.filter((d) => d.supersedes);
    return [...active, ...old];
  }, [decisions]);

  const hasContent = flow.length > 0 || decisions.length > 0 || openQuestions.length > 0;

  return (
    <div
      className="rounded-lg border border-white/10 bg-white/5 p-3 sm:p-4 space-y-3"
      data-testid="workbench-systemmap-panel"
    >
      <header className="flex items-center gap-2">
        <GitBranch className="w-4 h-4 text-cyan-300" />
        <h2 className="text-sm font-semibold">System map</h2>
        <span className="text-[10px] text-[#647087]">
          {flow.length} stage{flow.length === 1 ? "" : "s"}
          {decisions.length > 0 && ` · ${decisions.filter((d) => !d.supersedes).length} active decision${decisions.filter((d) => !d.supersedes).length === 1 ? "" : "s"}`}
          {openQuestions.length > 0 && ` · ${openQuestions.length} open Q${openQuestions.length === 1 ? "" : "s"}`}
        </span>
        <span className="text-[10px] text-[#475569] ml-auto inline-flex items-center gap-1">
          {loading && <Loader2 className="w-3 h-3 animate-spin" />}
          {updatedAt ? new Date(updatedAt).toLocaleTimeString() : ""}
        </span>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-[#647087] hover:text-white p-0.5"
          data-testid="workbench-systemmap-collapse"
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
      </header>

      {!collapsed && (
        <>
          {!hasContent ? (
            <p
              className="text-xs text-[#647087] italic flex items-center gap-1.5"
              data-testid="systemmap-empty"
            >
              <Droplets className="w-3 h-3 text-cyan-400/60" />
              Once you chat through the design with Hidro, the process chain, sizing decisions and open questions will appear here automatically.
            </p>
          ) : (
            <div className="space-y-3">
              {/* Process chain */}
              {flow.length > 0 && (
                <section data-testid="systemmap-flow">
                  <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-1.5">
                    Process chain
                  </div>
                  <div className="flex items-start gap-1.5 overflow-x-auto pb-2 -mx-1 px-1">
                    {flow.map((stage, idx) => (
                      <StageNode key={`flow-${idx}`} stage={stage} idx={idx} total={flow.length} />
                    ))}
                  </div>
                </section>
              )}

              {/* Open questions — surfaced prominently so the rep sees
                  what blocks Hidro before scrolling to decisions. */}
              {openQuestions.length > 0 && (
                <section data-testid="systemmap-open-questions">
                  <div className="text-[10px] uppercase tracking-wider text-amber-300/80 font-bold mb-1.5 flex items-center gap-1">
                    <HelpCircle className="w-3 h-3" /> Open questions for the rep
                  </div>
                  <ul className="space-y-1">
                    {openQuestions.slice(-8).map((q, idx) => (
                      <li
                        key={`q-${idx}`}
                        className="text-[11px] text-amber-100/90 bg-amber-500/5 border border-amber-400/20 rounded px-2 py-1 flex items-start gap-1.5"
                      >
                        <AlertCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-300" />
                        <span className="break-words">{typeof q === "string" ? q : _asText(q?.text || q)}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Decisions ledger — active first, then superseded. */}
              {decisions.length > 0 && (
                <section data-testid="systemmap-decisions">
                  <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-1.5">
                    Engineering decisions ({orderedDecisions.filter((d) => !d.supersedes).length} active
                    {orderedDecisions.some((d) => d.supersedes) ? `, ${orderedDecisions.filter((d) => d.supersedes).length} superseded` : ""})
                  </div>
                  <ul className="space-y-0.5 max-h-[260px] overflow-y-auto pr-1">
                    {orderedDecisions.map((d, idx) => (
                      <DecisionRow key={`dec-${idx}`} decision={d} />
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}

          <p className="text-[10px] text-[#647087] italic flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-purple-300" />
            Read-only — updated by Hidro after every chat turn. To change anything, just tell Hidro in the chat above.
          </p>
        </>
      )}
    </div>
  );
}
